import React, { useState, useRef, useEffect } from 'react';
import './DanceStyleSearch.css';

const POPULAR_STYLES = ['Bachata', 'Salsa', 'Kizomba', 'Zouk', 'West Coast Swing', 'Tango'];

const DanceStyleSearch = ({
  value,
  onChange,
  label = 'Dance Style',
  placeholder = 'Search or select a dance style...',
  required = false,
  error = '',
  apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPopular, setShowPopular] = useState(true);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set display value when value prop changes
  useEffect(() => {
    if (value) {
      setSearchInput(value);
      setShowPopular(false);
    } else {
      // Clear input when value is reset
      setSearchInput('');
      setShowPopular(true);
    }
  }, [value]);

  const fetchDanceStyles = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowPopular(true);
      return;
    }

    setLoading(true);
    setShowPopular(false);

    try {
      const response = await fetch(
        `${apiBase}/events/dance-styles?search=${encodeURIComponent(query)}`
      );
      const data = await response.json();

      if (data.success && data.data) {
        setSuggestions(data.data);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching dance styles:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);
    setShowDropdown(true);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Debounce API call
    searchTimeout.current = setTimeout(() => {
      fetchDanceStyles(inputValue);
    }, 200);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    if (!searchInput) {
      setShowPopular(true);
    }
  };

  const handleSelectStyle = (style) => {
    const styleName = typeof style === 'string' ? style : style.name;
    setSearchInput(styleName);
    onChange(styleName);
    setShowDropdown(false);
    setShowPopular(false);
    setSuggestions([]);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchInput('');
    onChange('');
    setSuggestions([]);
    setShowPopular(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchInput.trim()) {
      e.preventDefault();
      // If there are suggestions, select the first one
      if (suggestions.length > 0) {
        handleSelectStyle(suggestions[0]);
      } else {
        // Otherwise use the input value directly
        handleSelectStyle(searchInput.trim());
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dance-style-search" ref={containerRef}>
      {label && (
        <label className="search-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div className={`search-input-container ${showDropdown ? 'open' : ''} ${error ? 'has-error' : ''}`}>
        <i className="bi bi-search search-icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          value={searchInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        {loading && <div className="search-spinner"></div>}
        {searchInput && !loading && (
          <button
            type="button"
            className="clear-btn"
            onClick={handleClear}
            title="Clear"
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {error && <div className="search-error">{error}</div>}

      {showDropdown && (
        <div className="search-dropdown">
          {/* Popular Styles Quick Select */}
          {showPopular && !loading && (
            <div className="popular-styles">
              <div className="popular-header">Popular Styles</div>
              <div className="popular-chips">
                {POPULAR_STYLES.map((style) => (
                  <button
                    key={style}
                    type="button"
                    className="popular-chip"
                    onClick={() => handleSelectStyle(style)}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Suggestions */}
          {!showPopular && suggestions.length > 0 && (
            <div className="suggestions-list">
              {suggestions.map((style, index) => (
                <button
                  key={style.name || index}
                  type="button"
                  className="suggestion-item"
                  onClick={() => handleSelectStyle(style)}
                >
                  <span className="style-name">{style.name}</span>
                  {style.count > 0 && (
                    <span className="style-count">{style.count} events</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!showPopular && !loading && suggestions.length === 0 && searchInput.trim() && (
            <div className="no-results">
              <p>No exact matches found</p>
              <button
                type="button"
                className="use-custom-btn"
                onClick={() => handleSelectStyle(searchInput.trim())}
              >
                Use "{searchInput.trim()}"
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="search-spinner"></div>
              <span>Searching...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DanceStyleSearch;
